import React, { useState, useEffect, useCallback, useRef } from 'react';
import MPDashboardLayout from '../../components/mp/MPDashboardLayout';
import { postsApi, exercisesApi, platformsApi, mediaApi } from '../../lib/mpApi';
import { SocialPostTemplate, isSocialPlatform } from '../../components/mp/SocialPostTemplates';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { TIMEZONES } from '../../lib/timezone';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
  Plus, Search, Edit, Trash2, Send, Loader2, FileText, FileUp, X, Film, CalendarClock, Eye, Hash,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (url) => url?.startsWith('/') ? `${BACKEND_URL}${url}` : url;

const MPSocialPostsPage = () => {
  const [posts, setPosts] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterExercise, setFilterExercise] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewPost, setPreviewPost] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    exercise_id: '', platform: '', content: '', status: 'draft',
    author: { name: '', handle: '', bio: '', followers: 0, is_verified: false },
    engagement: { likes: 0, comments: 0, shares: 0, retweets: 0, views: 0 },
    hashtags: [], verification_status: 'none', location: '', media_urls: [],
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [postsData, exData, platData] = await Promise.all([
        postsApi.getAll({ post_type: 'social_media' }),
        exercisesApi.getAll().catch(() => []),
        platformsApi.getAll().catch(() => []),
      ]);
      setPosts(Array.isArray(postsData) ? postsData.filter(p => p.post_type === 'social_media') : []);
      setExercises(Array.isArray(exData) ? exData : []);
      setPlatforms(Array.isArray(platData) ? platData.filter(p => isSocialPlatform(p.name) || p.category === 'custom' || p.category === 'regional') : []);
    } catch { toast.error('Failed to load posts'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = posts.filter(p => {
    if (search && !p.content?.toLowerCase().includes(search.toLowerCase()) && !p.author?.name?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterExercise !== 'all' && p.exercise_id !== filterExercise) return false;
    if (filterPlatform !== 'all' && p.platform?.toLowerCase() !== filterPlatform.toLowerCase()) return false;
    if (filterStatus !== 'all' && p.status !== filterStatus) return false;
    return true;
  });

  // Filter platforms by selected exercise's selected_platforms
  const selectedExObj = exercises.find(e => e.exercise_id === formData.exercise_id);
  const formPlatforms = selectedExObj?.selected_platforms?.length > 0
    ? platforms.filter(p => selectedExObj.selected_platforms.includes(p.platform_id) || selectedExObj.selected_platforms.includes(p.name))
    : platforms;

  const openCreate = () => {
    setEditingPost(null);
    setPreviewPost(null);
    setFormData({
      exercise_id: exercises[0]?.exercise_id || '', platform: '', content: '', status: 'draft',
      author: { name: '', handle: '', bio: '', followers: 0, is_verified: false },
      engagement: { likes: 0, comments: 0, shares: 0, retweets: 0, views: 0 },
      hashtags: [], verification_status: 'none', location: '', media_urls: [],
      schedule: null,
    });
    setSelectedFiles([]);
    setShowDialog(true);
  };

  const openEdit = (post) => {
    setEditingPost(post);
    setPreviewPost(null);
    setFormData({
      exercise_id: post.exercise_id, platform: post.platform, content: post.content, status: post.status,
      author: post.author || { name: '', handle: '', bio: '', followers: 0, is_verified: false },
      engagement: post.engagement || { likes: 0, comments: 0, shares: 0, retweets: 0, views: 0 },
      hashtags: post.hashtags || [], verification_status: post.verification_status || 'none',
      location: post.location || '', media_urls: post.media_urls || [],
      schedule: post.schedule || null,
    });
    setSelectedFiles([]);
    setShowDialog(true);
  };

  const handleFileUpload = async () => {
    const uploadedUrls = [];
    for (const file of selectedFiles) {
      try {
        const result = await mediaApi.upload(file);
        uploadedUrls.push(result.file_url);
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    return uploadedUrls;
  };

  const handleSave = async () => {
    if (!formData.exercise_id || !formData.platform || !formData.content) {
      toast.error('Exercise, platform and content are required'); return;
    }
    setSaving(true);
    try {
      let mediaUrls = [...formData.media_urls];
      if (selectedFiles.length > 0) {
        const uploaded = await handleFileUpload();
        mediaUrls = [...mediaUrls, ...uploaded];
      }
      const payload = { ...formData, media_urls: mediaUrls, post_type: 'social_media' };
      if (editingPost) {
        await postsApi.update(editingPost.post_id, payload);
        toast.success('Post updated');
      } else {
        await postsApi.create(payload);
        toast.success('Post created');
      }
      setShowDialog(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (post) => {
    if (!window.confirm('Delete this post?')) return;
    try { await postsApi.delete(post.post_id); toast.success('Deleted'); loadData(); }
    catch { toast.error('Delete failed'); }
  };

  const handlePublish = async (post) => {
    try { await postsApi.publish(post.post_id); toast.success('Published'); loadData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Publish failed'); }
  };

  const handleUnpublish = async (post) => {
    if (!window.confirm(`Unpublish this post? It will return to draft and disappear from Live Display.`)) return;
    try { await postsApi.unpublish(post.post_id); toast.success('Unpublished'); loadData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Unpublish failed'); }
  };

  const handleFileSelect = (e) => {
    setSelectedFiles(prev => [...prev, ...Array.from(e.target.files)]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Build live preview
  const livePreview = {
    ...formData,
    post_id: 'preview',
    post_type: 'social_media',
    timestamp: new Date().toISOString(),
    media_urls: [
      ...formData.media_urls,
      ...selectedFiles.filter(f => f.type.startsWith('image/')).map(f => URL.createObjectURL(f)),
    ],
  };

  return (
    <MPDashboardLayout title="Social Posts">
      <div className="space-y-6" data-testid="mp-social-posts-page">
        {/* Toolbar */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search posts..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 bg-muted/50 border-input placeholder:text-muted-foreground" data-testid="mp-social-search" />
          </div>
          <Select value={filterExercise} onValueChange={setFilterExercise}>
            <SelectTrigger className="w-40 bg-muted/50 border-input"><SelectValue placeholder="Exercise" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Exercises</SelectItem>{exercises.map(e => <SelectItem key={e.exercise_id} value={e.exercise_id}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterPlatform} onValueChange={setFilterPlatform}>
            <SelectTrigger className="w-36 bg-muted/50 border-input"><SelectValue placeholder="Platform" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Platforms</SelectItem>{platforms.map(p => <SelectItem key={p.platform_id} value={p.name}>{p.display_name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-32 bg-muted/50 border-input"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="draft">Draft</SelectItem><SelectItem value="published">Published</SelectItem><SelectItem value="scheduled">Scheduled</SelectItem></SelectContent>
          </Select>
          <Button onClick={openCreate} className="bg-red-600 hover:bg-red-700 text-foreground" data-testid="mp-create-social-post-btn">
            <Plus className="w-4 h-4 mr-2" /> New Post
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total Posts', value: posts.length, color: 'text-foreground' },
            { label: 'Published', value: posts.filter(p => p.status === 'published').length, color: 'text-emerald-400' },
            { label: 'Drafts', value: posts.filter(p => p.status === 'draft').length, color: 'text-muted-foreground' },
            { label: 'Scheduled', value: posts.filter(p => p.status === 'scheduled').length, color: 'text-amber-400' },
          ].map(s => (
            <div key={s.label} className="bg-muted/40 border border-border rounded-lg px-4 py-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Posts Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No social posts found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map(post => (
              <div key={post.post_id} className="relative group" data-testid={`mp-social-post-${post.post_id}`}>
                <SocialPostTemplate post={post} platform={post.platform} />
                {/* Status badge */}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Badge className={`text-[10px] ${
                    post.status === 'published' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' :
                    post.status === 'scheduled' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                    'bg-muted0/20 text-muted-foreground border-border/30'
                  } border`}>{post.status}</Badge>
                </div>
                {/* Action buttons */}
                <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  {post.status === 'draft' && (
                    <Button size="sm" onClick={() => handlePublish(post)} className="bg-emerald-600 hover:bg-emerald-700 text-foreground h-7 text-xs">
                      <Send className="w-3 h-3 mr-1" /> Publish
                    </Button>
                  )}
                  {post.status === 'published' && (
                    <Button size="sm" onClick={() => handleUnpublish(post)} variant="outline" className="h-7 text-xs" data-testid={`unpublish-${post.post_id}`}>
                      Unpublish
                    </Button>
                  )}
                  <Button size="sm" variant="secondary" onClick={() => openEdit(post)} className="h-7 bg-muted hover:bg-muted/80"><Edit className="w-3 h-3" /></Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(post)} className="h-7"><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog with Live Preview */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto ">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-foreground">{editingPost ? 'Edit Social Post' : 'Create Social Post'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-4">
            {/* Form */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Exercise</Label>
                  <Select value={formData.exercise_id} onValueChange={v => setFormData({...formData, exercise_id: v})}>
                    <SelectTrigger className="bg-muted border-input"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{exercises.map(e => <SelectItem key={e.exercise_id} value={e.exercise_id}>{e.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Platform</Label>
                  <Select value={formData.platform} onValueChange={v => setFormData({...formData, platform: v})}>
                    <SelectTrigger className="bg-muted border-input"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{formPlatforms.map(p => <SelectItem key={p.platform_id} value={p.name}>{p.display_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Content</Label>
                <Textarea value={formData.content} onChange={e => setFormData({...formData, content: e.target.value})}
                  placeholder="What's happening?" rows={4}
                  className="bg-muted border-input placeholder:text-muted-foreground"
                  data-testid="mp-social-content-input" />
              </div>

              {/* Author */}
              <div className="bg-muted/50 rounded-lg p-3 border border-input space-y-3">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Author Profile</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Display Name" value={formData.author.name}
                    onChange={e => setFormData({...formData, author: {...formData.author, name: e.target.value}})}
                    className="bg-muted border-input h-8 text-sm" />
                  <Input placeholder="@handle" value={formData.author.handle}
                    onChange={e => setFormData({...formData, author: {...formData.author, handle: e.target.value}})}
                    className="bg-muted border-input h-8 text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input type="number" placeholder="Followers" value={formData.author.followers}
                    onChange={e => setFormData({...formData, author: {...formData.author, followers: parseInt(e.target.value) || 0}})}
                    className="bg-muted border-input h-8 text-sm" />
                  <div className="flex items-center gap-2">
                    <Switch checked={formData.author.is_verified}
                      onCheckedChange={v => setFormData({...formData, author: {...formData.author, is_verified: v}, verification_status: v ? 'verified' : 'none'})} />
                    <Label className="text-xs text-muted-foreground">Verified</Label>
                  </div>
                </div>
              </div>

              {/* Engagement */}
              <div className="bg-muted/50 rounded-lg p-3 border border-input space-y-3">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Engagement</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: 'likes', label: 'Likes' },
                    { key: 'comments', label: 'Comments' },
                    { key: 'shares', label: 'Shares' },
                    { key: 'retweets', label: 'Retweets' },
                    { key: 'views', label: 'Views' },
                  ].map(field => (
                    <Input key={field.key} type="number" placeholder={field.label} value={formData.engagement[field.key]}
                      onChange={e => setFormData({...formData, engagement: {...formData.engagement, [field.key]: parseInt(e.target.value) || 0}})}
                      className="bg-muted border-input h-8 text-sm" />
                  ))}
                </div>
              </div>

              {/* Hashtags */}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                  <Hash className="w-3 h-3" /> Hashtags
                </Label>
                <Input value={formData.hashtags.join(', ')}
                  onChange={e => setFormData({...formData, hashtags: e.target.value.split(',').map(h => h.trim()).filter(Boolean)})}
                  placeholder="tag1, tag2" className="bg-muted border-input" />
              </div>

              {/* Schedule */}
              <div className="bg-muted/50 rounded-lg p-3 border border-input space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <CalendarClock className="w-3.5 h-3.5" /> Schedule
                  </Label>
                  <Switch checked={formData.status === 'scheduled'}
                    onCheckedChange={v => setFormData({
                      ...formData, status: v ? 'scheduled' : 'draft',
                      schedule: v ? { scheduled: true, publish_datetime: '', timezone: 'UTC' } : null,
                    })}
                    data-testid="mp-social-schedule-toggle" />
                </div>
                {formData.status === 'scheduled' && (
                  <div className="grid grid-cols-2 gap-3">
                    <Input type="datetime-local" value={formData.schedule?.publish_datetime || ''}
                      onChange={e => setFormData({...formData, schedule: { ...formData.schedule, scheduled: true, publish_datetime: e.target.value }})}
                      className="bg-muted border-input h-8 text-sm"
                      data-testid="mp-social-schedule-datetime" />
                    <Select value={formData.schedule?.timezone || 'UTC'}
                      onValueChange={v => setFormData({...formData, schedule: { ...formData.schedule, timezone: v }})}>
                      <SelectTrigger className="bg-muted border-input h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TIMEZONES.map(tz => (
                          <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Media */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Media</Label>
                <div className="border-2 border-dashed border-input rounded-lg p-4 text-center bg-muted/30">
                  <input ref={fileInputRef} type="file" multiple accept="image/*,video/*" onChange={handleFileSelect} className="hidden" />
                  <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}
                    className="border-input text-muted-foreground hover:bg-muted" data-testid="mp-social-upload-btn">
                    <FileUp className="w-4 h-4 mr-2" />Add Media
                  </Button>
                </div>
                {(selectedFiles.length > 0 || formData.media_urls.length > 0) && (
                  <div className="flex flex-wrap gap-2">
                    {formData.media_urls.map((url, i) => (
                      <div key={`url-${i}`} className="relative group/img w-16 h-16 border border-input rounded-lg overflow-hidden">
                        <img src={resolveUrl(url)} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => setFormData({...formData, media_urls: formData.media_urls.filter((_, idx) => idx !== i)})}
                          className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-foreground rounded-full flex items-center justify-center opacity-0 group-hover/img:opacity-100"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                    {selectedFiles.map((file, i) => (
                      <div key={`file-${i}`} className="relative group/img w-16 h-16 border border-input rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                        {file.type.startsWith('video/') ? <Film className="w-6 h-6 text-muted-foreground" /> : <img src={URL.createObjectURL(file)} alt="" className="w-full h-full object-cover" />}
                        <button onClick={() => setSelectedFiles(prev => prev.filter((_, idx) => idx !== i))}
                          className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-foreground rounded-full flex items-center justify-center opacity-0 group-hover/img:opacity-100"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Live Preview */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-4 h-4 text-muted-foreground" />
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Live Preview</Label>
              </div>
              <div className="bg-muted/30 rounded-lg border border-input p-4">
                {formData.platform ? (
                  <SocialPostTemplate post={livePreview} platform={formData.platform} />
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <p className="text-sm">Select a platform to see preview</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="border-t border-border pt-4">
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-input text-muted-foreground hover:bg-muted">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700 text-foreground" data-testid="mp-social-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MPDashboardLayout>
  );
};

export default MPSocialPostsPage;
